import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'reactstrap';

import images from 'assets/images';
import Button from 'components/App/Button';
import ColGroup from 'components/ColGroup';

import { convertIsoDateToString } from 'utils';

import Bcoin from 'assets/images/Bcoin.svg';
import styles from './DataTableListGift.module.scss';

const HEADS = [
  'Tên quà',
  'Loại quà',
  'Chiến dịch voucher',
  'Giá quy đổi',
  'Số lượng quà / Đã đổi',
  'Hạn sử dụng',
  'Trạng thái',
];

const COL_WIDTHS = [3.5, 1.5, 1.5, 1.2, 1.2, 1, 1];
const COL_WIDTH_SUM = COL_WIDTHS.reduce((sum, width) => sum + width, 0);

const GIFT_CATEGORY_LABEL = {
  VOUCHER: 'Voucher',
  EXCLUSIVE: 'Quà Độc Quyền',
};

const GIFT_STATUS_LABEL = {
  PUBLISHED: 'Công khai',
  PUBLIC: 'Công khai',
  DRAFT: 'Đã ẩn',
  EXPIRED: 'Hết hạn',
};

const GIFT_STATUS_CLASS = {
  PUBLISHED: 'Status__Public',
  PUBLIC: 'Status__Public',
  DRAFT: 'Status__Hidden',
  EXPIRED: 'Status__Expired',
};

const coinFormatter = new Intl.NumberFormat('vi-VN');

const formatCoinValue = (value) => coinFormatter.format(Number(value) || 0);

const getGiftExpiryAt = (gift) => (
  gift?.voucherCampaign?.voucherExpiryAt || gift?.expiryAt || gift?.expiredAt
);

const getGiftImage = (gift) => {
  const firstImage = gift?.images?.[0];
  if (typeof firstImage === 'string') return firstImage;
  return firstImage?.image || images.locationImage.default;
};

const getGiftTypeLabel = (gift) => {
  if (gift?.isFlashSale) return 'Quà giới hạn';
  return GIFT_CATEGORY_LABEL[gift?.category] || gift?.category || '-';
};

const getGiftTypeSubLabel = (gift) => {
  if (!gift?.isFlashSale) return null;
  const start = convertIsoDateToString(gift?.limitStartAt || gift?.flashSaleStartAt);
  const end = convertIsoDateToString(gift?.limitEndAt || gift?.flashSaleEndAt);
  if (!start && !end) return null;
  return `${start}${start && end ? ' - ' : ''}${end}`;
};

const getGiftStatusKey = (gift) => {
  if (gift?.status === 'EXPIRED' || gift?.isExpired) return 'EXPIRED';
  if (gift?.status === 'DRAFT' || gift?.status === 'HIDDEN') return 'DRAFT';
  if (gift?.status === 'PUBLIC') return 'PUBLISHED';
  return 'PUBLISHED';
};

const formatGiftId = (gift) => {
  const id = gift?.giftId;
  if (!id) return '';
  return `ID: #${id}`;
};

function DataTableListGift(props) {
  const {
    gifts,
    onDeleteGift,
    onViewGiftDetail,
  } = props;

  const renderActions = useCallback((gift) => (
    <div className="Action d-flex gap-3">
      <Button
        outline
        classes="Action__Button Action__Button__Danger"
        onClick={() => onDeleteGift?.(gift)}
      >
        Xoá
      </Button>

      <Button
        outline
        classes="Action__Button Action__Button__Link"
        onClick={() => onViewGiftDetail?.(gift)}
      >
        Xem chi tiết
      </Button>
    </div>
  ), [onDeleteGift, onViewGiftDetail]);

  return (
    <div
      className={styles.Component}
      style={{ '--gift-table-cols': COL_WIDTH_SUM }}
    >
      {gifts.length === 0 ? (
        <div className="NoData">Chưa có dữ liệu</div>
      ) : (
        <Table responsive className="Table">
          <ColGroup
            list={COL_WIDTHS}
            containerWidth={COL_WIDTH_SUM}
            paddingX={0}
          />
          <thead className="Table__Header">
            <tr className="Table__Header__Row">
              {HEADS.map((head) => (
                <th key={head} className="Table__Header__Row__Content">
                  {head}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="Table__Body">
            {gifts.map((gift) => {
              const statusKey = getGiftStatusKey(gift);
              const typeSubLabel = getGiftTypeSubLabel(gift);
              const expiryAt = getGiftExpiryAt(gift);
              const expiryDateLabel = expiryAt
                ? convertIsoDateToString(expiryAt)
                : '';

              return (
                <tr className="Table__Body__Row" key={gift.id || gift._id}>
                  <td className="Table__Body__Row__Content">
                    <div className="GiftName">
                      <img
                        src={getGiftImage(gift)}
                        alt={gift.name}
                        className="GiftName__Image"
                      />

                      <div className="GiftName__Info">
                        <div className="GiftName__TitleAndId">
                          <span className="GiftName__Title">{gift.name}</span>
                          <span className="GiftName__Id">{formatGiftId(gift)}</span>
                        </div>
                        {renderActions(gift)}
                      </div>
                    </div>
                  </td>

                  <td className="Table__Body__Row__Content">
                    <div className="GiftType">
                      <span className="GiftType__Label">{getGiftTypeLabel(gift)}</span>
                      {typeSubLabel && (
                        <span className="GiftType__DateRange">{typeSubLabel}</span>
                      )}
                    </div>
                  </td>

                  <td className="Table__Body__Row__Content">
                    {gift.voucherCampaign?.name && (
                      <div className="VoucherCampaign">
                        {gift.voucherCampaign.name}
                      </div>
                    )}
                  </td>

                  <td className="Table__Body__Row__Content">
                    <div className="Bcoin">
                      {/* <span className="Bcoin__Icon" aria-hidden /> */}
                      <img src={Bcoin} alt="" />
                      <span>
                        {formatCoinValue(gift.coin ?? gift.redeemPoints)}
                        {' '}
                        Bcoin
                      </span>
                    </div>
                  </td>

                  <td className="Table__Body__Row__Content">
                    <div className="Stock">
                      {gift.quantity ?? 0}
                      {' / '}
                      {gift.exchangedQuantity ?? gift.redeemedCount ?? 0}
                    </div>
                  </td>

                  <td className="Table__Body__Row__Content">
                    {expiryDateLabel && (
                      <div className="ExpiryDate">
                        {expiryDateLabel}
                      </div>
                    )}
                  </td>

                  <td className="Table__Body__Row__Content">
                    <span className={`Status ${GIFT_STATUS_CLASS[statusKey]}`}>
                      {GIFT_STATUS_LABEL[statusKey]}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </div>
  );
}

DataTableListGift.propTypes = {
  gifts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    _id: PropTypes.string,
    name: PropTypes.string,
    giftCode: PropTypes.string,
    code: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        image: PropTypes.string,
      }),
    ])),
    category: PropTypes.string,
    isFlashSale: PropTypes.bool,
    isExpired: PropTypes.bool,
    coin: PropTypes.number,
    redeemPoints: PropTypes.number,
    quantity: PropTypes.number,
    exchangedQuantity: PropTypes.number,
    redeemedCount: PropTypes.number,
    expiryAt: PropTypes.string,
    expiredAt: PropTypes.string,
    voucherCampaign: PropTypes.shape({
      name: PropTypes.string,
      voucherExpiryAt: PropTypes.string,
    }),
    limitStartAt: PropTypes.string,
    limitEndAt: PropTypes.string,
    flashSaleStartAt: PropTypes.string,
    flashSaleEndAt: PropTypes.string,
    status: PropTypes.string,
  })),
  onDeleteGift: PropTypes.func,
  onViewGiftDetail: PropTypes.func,
};

DataTableListGift.defaultProps = {
  gifts: [],
  onDeleteGift: undefined,
  onViewGiftDetail: undefined,
};

export default memo(DataTableListGift);
